<template>
  <div class="onboarding">
    <m-dialog
      v-model:value="showCompanyInfo"
      :max-width="$modalSizes.xl"
      no-padding
      keep-height
      hide-header
      top="7rem"
      hide-footer
      keep-open-on-mask-click
    >
      <company-info-editor
        :company-info="companyInfo"
        fullscreen
        padding-top
        disabled
        show-footer
        :ok-text="$t('onboarding.continue')"
        :ok-handler="continueFromCompanyInfo"
      />
    </m-dialog>
    <template v-if="currentStep !== null">
      <div class="_content">
        <div class="_header">
          <div class="_title">
            <h1>
              {{ currentStep.title }}
            </h1>
          </div>
          <div class="_sub-title">
            {{ currentStep.subTitle }}
          </div>
        </div>
        <div class="_steps">
          <personal-infos
            v-if="currentStep.number === 1"
            :is-last-step="steps.length === currentStep.number"
          />
          <account-infos
            v-if="currentStep.number === 2"
            @done="handleAccountInfosDone"
          />
        </div>
        <footer
          style="padding-bottom: 42px;"
          class="_footer"
        >
          <div class="_inner">
            <div>
              <i18n-t keypath="onboarding.loggedInAs">
                <template #email>
                  <span style="font-weight: 500;">{{ email(loggedInUser) }}</span>
                </template>
                <template #link>
                  <m-link
                    class="_link"
                    @click="EventBus.$emit('logout')"
                  >
                    {{ $t('onboarding.logInAsDifferentUser') }}
                  </m-link>
                </template>
              </i18n-t>
            </div>
          </div>
        </footer>
        <template v-if="!$store.state.breakpoint.smAndDown">
          <img
            class="_img-left"
            src="@/assets/img/illustrations/moon-3d.webp"
          >
          <img
            class="_img-right"
            src="@/assets/img/illustrations/moon-3d.webp"
          >
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import AccountInfos from '@/components/onboarding/AccountInfos.vue';
import CompanyInfoEditor from '@/components/CompanyInfoEditor.vue';
import PersonalInfos from '@/components/onboarding/PersonalInfos.vue';
import useAccess from '@/composables/access/access';
import useAppStart from '@/composables/app-start/use-app-start';
import useCompanyInfo from '@/composables/logged-in-user-account/company-info';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import usePageVisits from '@/composables/page-visits';
import usePersonalAppSettings from '@/composables/logged-in-user/personal-app-settings';
import { EventBus } from '@/lib/event-bus';
import { GATE, gateDecision } from '@/composables/app-start/gate';
import { email } from 'shared/composables/user';
import { logCatch } from '@/lib/logger/logger';
import { routeName } from 'shared/constants.json';
import { showOnboarding } from '@/lib/onboarding/onboarding';

export default {
  name: 'Onboarding',
  components: { AccountInfos, PersonalInfos, CompanyInfoEditor },
  setup() {
    const pageVisitsService = usePageVisits();
    const { start: startApp } = useAppStart(pageVisitsService);
    const { accountHasFeature } = useAccess();
    const { loggedInUser, activateMembership } = useLoggedInUser();
    const { personalAppSettings } = usePersonalAppSettings(loggedInUser);
    const { loggedInUserAccount } = useLoggedInUserAccount();
    const { companyInfo, showCompanyInfoInOnboarding } = useCompanyInfo();

    return {
      accountHasFeature,
      loggedInUser,
      activateMembership,
      personalAppSettings,
      loggedInUserAccount,
      companyInfo,
      showCompanyInfoInOnboarding,
      startApp,
      email,
    };
  },
  data() {
    return { loading: false, EventBus, showCompanyInfo: false };
  },
  computed: {
    currentStep() {
      const gate = gateDecision({ user: this.loggedInUser, account: this.loggedInUserAccount });
      if (gate === GATE.PROFILE) {
        return this.steps[0];
      }
      if (gate === GATE.ACCOUNT) {
        return this.steps[1];
      }
      return null;
    },
    steps() {
      const steps = [
        {
          title: this.$t('onboarding.personalInfosTitle'),
          subTitle: this.$t('onboarding.personalInfosSubTitle'),
          number: 1,
        },
      ];
      if (this.loggedInUserAccount.accountSettings.accountInfosCompleted === null) {
        steps.push(
          {
            title: this.$t('onboarding.accountInfosTitle'),
            subTitle: this.$t('onboarding.accountInfosSubTitle'),
            number: 2,
          },
        );
      }
      return steps;
    },
  },
  methods: {
    continueFromCompanyInfo() {
      return this.activateMembership(this.loggedInUser)
        .then(() => this.goToApp())
        .catch(logCatch(() => {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
        }));
    },
    handleAccountInfosDone() {
      this.goToApp();
    },
    goToApp() {
      if (this.showCompanyInfoInOnboarding && !this.showCompanyInfo) {
        this.showCompanyInfo = true;
        return;
      }

      this.startApp().then(() => {
        this.$router.push({ name: routeName.goalsExplorer });
      })
        .catch(logCatch(() => {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
        }));
    },
  },
  watch: {
    currentStep(val) {
      if (val === null) {
        this.goToApp();
      }
    },
  },
  created() {
    if (!showOnboarding({
      user: this.loggedInUser,
      account: this.loggedInUserAccount,
      showCompanyInfo: this.showCompanyInfoInOnboarding,
    })) {
      this.$router.replace({ name: routeName.goalsExplorer });
      return;
    }

    if (this.currentStep === null) {
      this.goToApp();
    }
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .onboarding {
    overflow: hidden;

    ._content {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      width: 100%;
      height: var(--viewport-height-100);
      background-color: $side-nav-background-color;

      ._header {
        padding-top: 7rem;

        ._title {
          h1 {
            margin: 0 0 1.6rem;
            text-align: center;
          }
        }

        ._sub-title {
          font-size: $font-size-5;
          color: $font-color-secondary;
          text-align: center;
        }
      }

      ._img-left {
        position: absolute;
        top: 22.4rem;
        left: -15rem;
        height: 29.4rem;
      }

      ._img-right {
        position: absolute;
        right: -12rem;
        top: 44.8rem;
        height: 24.4rem;
        transform: rotate(45deg)
      }
    }

    ._footer {
      width: 50rem;
      max-width: 90%;
      margin: 0 auto;

      ._inner {
        font-size: $font-size-3;
        color: $font-color-tertiary;
        text-align: center;

        ._link {
          color: inherit;
          text-decoration: underline;
        }
      }
    }
  }
</style>
